import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { dms } from "./api";
import { PageHeader } from "./OwnerPages";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Store, UserCog, Network, Plus, X, ChevronRight, ChevronDown, ArrowRight, Trash2, Search, Loader2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const GOLD_BTN = "bg-[#a67c00] hover:bg-[#8a6800] text-white";

function Select({ placeholder, options, value, onChange }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-amber-300"
    >
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.name || o.email || o.id}</option>)}
    </select>
  );
}

function ConnectCard({ title, leftLabel, leftOptions, leftValue, onLeftChange,
                        rightLabel, rightOptions, rightValue, onRightChange, onConnect }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-bold text-slate-500 uppercase mb-3">{title}</div>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Select placeholder={leftLabel} options={leftOptions} value={leftValue} onChange={onLeftChange} />
        </div>
        <ArrowRight size={16} className="text-slate-300 shrink-0" />
        <div className="flex-1">
          <Select placeholder={rightLabel} options={rightOptions} value={rightValue} onChange={onRightChange} />
        </div>
      </div>
      <Button className={`${GOLD_BTN} w-full mt-3`} size="sm" onClick={onConnect}>
        <Plus size={14} className="mr-1" /> Connect
      </Button>
    </Card>
  );
}

// ── Box-style cards for the saved hierarchy (matches the earlier box design) ──
function SpBox({ sp, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 min-w-[160px]">
      <UserCog size={15} className="text-sky-600 shrink-0" />
      <span className="text-sm font-medium text-sky-800 truncate flex-1">{sp.name}</span>
      {onRemove && (
        <button onClick={onRemove} title="Disconnect" className="text-sky-300 hover:text-rose-500 shrink-0">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function RetBox({ r }) {
  return (
    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 min-w-[160px]">
      <Users size={15} className="text-emerald-600 shrink-0" />
      <span className="text-sm font-medium text-emerald-800 truncate">{r.name}</span>
    </div>
  );
}

// A vertical connector trunk with a horizontal stub line to each child box —
// this is what makes it look like the boxes are actually "wired together"
// instead of just grouped under a label.
function ConnectorGroup({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="ml-2 pl-5 relative flex flex-col gap-3 border-l-2 border-slate-300">
      {items.map((child, i) => (
        <div key={i} className="relative">
          <span className="absolute -left-5 top-5 w-5 h-0.5 bg-slate-300" />
          {child}
        </div>
      ))}
    </div>
  );
}

function DistBox({ node, tlId, onUnassignTlDist }) {
  return (
    <div className="border-2 border-amber-200 rounded-xl p-3 bg-amber-50/40 min-w-[220px] inline-block">
      <div className="flex items-center gap-2">
        <Store size={16} className="text-[#a67c00] shrink-0" />
        <span className="font-bold text-slate-800 text-sm truncate flex-1">{node.name}</span>
        {node.region && <span className="text-[10px] text-slate-400 shrink-0">{node.region}</span>}
        {tlId && (
          <button onClick={() => onUnassignTlDist(tlId, node.id)} title="Remove from Team Leader"
            className="text-slate-300 hover:text-rose-500 shrink-0"><X size={14} /></button>
        )}
      </div>
      {(node.retailers || []).length > 0 && (
        <ConnectorGroup>
          {node.retailers.map(r => <RetBox key={r.id} r={r} />)}
        </ConnectorGroup>
      )}
    </div>
  );
}

function TlBox({ tl, onUnassignRmTl, onUnassignTlSp, onUnassignTlDist, rmId, onDeleteBranch }) {
  const children = [
    ...(tl.salespersons || []).map(sp => (
      <SpBox key={`sp-${sp.id}`} sp={sp} onRemove={() => onUnassignTlSp(tl.id, sp.id)} />
    )),
    ...(tl.distributors || []).map(d => (
      <DistBox key={`d-${d.id}`} node={d} tlId={tl.id} onUnassignTlDist={onUnassignTlDist} />
    )),
  ];
  return (
    <div>
      <div className="border-2 border-indigo-200 rounded-xl p-3 bg-indigo-50/40 inline-flex items-center gap-2">
        <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold shrink-0">TL</span>
        <span className="font-bold text-slate-900 text-sm truncate">{tl.name}</span>
        {rmId && (
          <button onClick={() => onUnassignRmTl(rmId, tl.id)} title="Remove Team Leader from RM"
            className="text-slate-300 hover:text-rose-500 shrink-0"><X size={14} /></button>
        )}
        <button
          onClick={() => onDeleteBranch(tl, rmId)}
          title="Delete this whole branch"
          className="ml-1 flex items-center gap-1 text-[10px] font-semibold text-rose-500 hover:text-white hover:bg-rose-500 border border-rose-200 hover:border-rose-500 rounded-md px-2 py-1 shrink-0 transition-colors"
        >
          <Trash2 size={11} /> Delete
        </button>
      </div>
      {children.length > 0 ? (
        <ConnectorGroup>{children}</ConnectorGroup>
      ) : (
        <div className="ml-2 pl-5 mt-2 text-[11px] text-slate-400">No connections yet</div>
      )}
    </div>
  );
}

function AssignSalespersonCard({ distributors, retailers }) {
  const [dist, setDist] = useState("");
  const [ret, setRet] = useState("");
  const [tl, setTl] = useState("");
  const [sp, setSp] = useState("");
  const [tls, setTls] = useState([]);
  const [sps, setSps] = useState([]);
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const distRetailers = useMemo(
    () => (retailers || []).filter(r => r.distributor_id === dist),
    [retailers, dist]
  );

  const loadInfo = useCallback(async (rid) => {
    if (!rid) { setInfo(null); return; }
    try { setInfo(await dms.ownerRetailerAssignInfo(rid)); }
    catch (e) { setInfo(null); }
  }, []);

  // Distributor change -> reset everything below, load its Team Leaders
  useEffect(() => {
    setRet(""); setTl(""); setSp(""); setTls([]); setSps([]); setInfo(null);
    if (!dist) return;
    dms.ownerDistTeamLeaders(dist)
      .then(r => setTls(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load Team Leaders"));
  }, [dist]);

  // Team Leader change -> reset salesperson, load that TL's salespersons
  useEffect(() => {
    setSp(""); setSps([]);
    if (!dist || !tl) return;
    dms.ownerTlSalespersons(tl, dist)
      .then(r => setSps(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load Salespersons"));
  }, [tl, dist]);

  // Retailer change -> show current salesperson
  useEffect(() => { loadInfo(ret); }, [ret, loadInfo]);

  const save = async () => {
    if (!dist || !ret || !tl || !sp) return toast.error("Select Distributor, Retailer, Team Leader and Salesperson");
    setSaving(true);
    try {
      const r = await dms.ownerAssignSalesperson(ret, tl, sp);
      toast.success(r && r.changed === false ? "Already assigned to this salesperson" : "Salesperson assigned");
      setSp("");
      await loadInfo(ret);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const selCls = "w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-400";
  const lblCls = "text-[11px] font-semibold text-slate-500 uppercase mb-1";

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <UserCog size={16} className="text-[#a67c00]" />
        <h3 className="font-bold text-slate-900">Retailer → Salesperson</h3>
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <div>
          <div className={lblCls}>1. Distributor</div>
          <select className={selCls} value={dist} onChange={e => setDist(e.target.value)}>
            <option value="">Select Distributor</option>
            {(distributors || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <div className={lblCls}>2. Retailer</div>
          <select className={selCls} value={ret} disabled={!dist} onChange={e => setRet(e.target.value)}>
            <option value="">{dist ? "Select Retailer" : "Select Distributor first"}</option>
            {distRetailers.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <div className={lblCls}>3. Team Leader</div>
          <select className={selCls} value={tl} disabled={!dist} onChange={e => setTl(e.target.value)}>
            <option value="">{!dist ? "Select Distributor first" : (tls.length ? "Select Team Leader" : "No Team Leader")}</option>
            {tls.map(t => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
          </select>
        </div>
        <div>
          <div className={lblCls}>4. Salesperson</div>
          <select className={selCls} value={sp} disabled={!tl} onChange={e => setSp(e.target.value)}>
            <option value="">{!tl ? "Select Team Leader first" : (sps.length ? "Select Salesperson" : "No Salesperson")}</option>
            {sps.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <div className="text-xs text-slate-500">
          {info ? <>Currently assigned: <span className="font-bold text-slate-800">{info.salesperson_name || "-"}</span></> : "Select a retailer to see the current salesperson"}
        </div>
        <Button onClick={save} disabled={saving || !sp}>{saving ? "Saving..." : "Assign"}</Button>
      </div>
    </Card>
  );
}

// Old "Connect" panels + box view. Kept as-is under the collapsible "Edit links"
// section; only loads when the section is opened.
function EditLinksSection({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retailers, setRetailers] = useState([]);

  const [rmTl, setRmTl] = useState({ rm: "", tl: "" });
  const [tlSp, setTlSp] = useState({ tl: "", sp: "" });
  const [tlDist, setTlDist] = useState({ tl: "", dist: "" });
  const [distRet, setDistRet] = useState({ dist: "", ret: "" });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [h, rl] = await Promise.all([
        dms.ownerHierarchy(),
        dms.listRetailers({ lite: 1 }).catch(() => ({ data: [] })),
      ]);
      setData(h);
      setRetailers(rl.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load hierarchy");
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = useMemo(
    () => data?.all || { regional_managers: [], team_leaders: [], salespersons: [], distributors: [] },
    [data]
  );

  const doAssignRmTl = async () => {
    if (!rmTl.rm || !rmTl.tl) return toast.error("Select Regional Manager and Team Leader");
    try {
      await dms.assignRmTl({ regional_manager_id: rmTl.rm, team_leader_id: rmTl.tl });
      toast.success("Connected");
      setRmTl({ rm: "", tl: "" });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doAssignTlSp = async () => {
    if (!tlSp.tl || !tlSp.sp) return toast.error("Select Team Leader and Salesperson");
    try {
      await dms.assignTlSp({ team_leader_id: tlSp.tl, salesperson_id: tlSp.sp });
      toast.success("Connected");
      setTlSp({ tl: "", sp: "" });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doAssignTlDist = async () => {
    if (!tlDist.tl || !tlDist.dist) return toast.error("Select Team Leader and Distributor");
    try {
      await dms.assignTlDist({ team_leader_id: tlDist.tl, distributor_id: tlDist.dist });
      toast.success("Connected");
      setTlDist({ tl: "", dist: "" });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doAssignDistRet = async () => {
    if (!distRet.dist || !distRet.ret) return toast.error("Select Distributor and Retailer");
    try {
      await dms.bulkAssignRetailers([distRet.ret], distRet.dist);
      toast.success("Connected");
      setDistRet({ dist: "", ret: "" });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const unassign = useCallback(async (fn, ...args) => {
    try { await fn(...args); toast.success("Disconnected"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [load, onChanged]);
  const unassignRmTl = (rm, tl) => unassign(dms.unassignRmTl, rm, tl);
  const unassignTlSp = (tl, sp) => unassign(dms.unassignTlSp, tl, sp);
  const unassignTlDist = (tl, did) => unassign(dms.unassignTlDist, tl, did);

  const deleteBranch = useCallback(async (tl, rmId) => {
    const spCount = (tl.salespersons || []).length;
    const distCount = (tl.distributors || []).length;
    const ok = window.confirm(
      `Delete "${tl.name}"'s entire branch?\n\nThis removes:\n- Its link to the Regional Manager (if any)\n- ${spCount} salesperson connection(s)\n- ${distCount} distributor connection(s)\n\nRetailers under those distributors will stay linked to the distributor.`
    );
    if (!ok) return;
    try {
      if (rmId) await dms.unassignRmTl(rmId, tl.id);
      for (const sp of tl.salespersons || []) {
        await dms.unassignTlSp(tl.id, sp.id);
      }
      for (const d of tl.distributors || []) {
        await dms.unassignTlDist(tl.id, d.id);
      }
      toast.success(`Deleted ${tl.name}'s branch`);
      load(); onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete branch");
    }
  }, [load, onChanged]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-xs text-slate-500">Pick from the dropdowns and hit Connect. Everything saves instantly.</p>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}>{busy ? "Loading..." : "Reload"}</Button>
      </div>

      <div className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1">
        <Network size={14} className="text-[#a67c00]" /> Build the hierarchy
      </div>
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <ConnectCard
          title="Regional Manager → Team Leader"
          leftLabel="Select Regional Manager" leftOptions={all.regional_managers} leftValue={rmTl.rm}
          onLeftChange={v => setRmTl(s => ({ ...s, rm: v }))}
          rightLabel="Select Team Leader" rightOptions={all.team_leaders} rightValue={rmTl.tl}
          onRightChange={v => setRmTl(s => ({ ...s, tl: v }))}
          onConnect={doAssignRmTl}
        />
        <ConnectCard
          title="Team Leader → Salesperson"
          leftLabel="Select Team Leader" leftOptions={all.team_leaders} leftValue={tlSp.tl}
          onLeftChange={v => setTlSp(s => ({ ...s, tl: v }))}
          rightLabel="Select Salesperson" rightOptions={all.salespersons} rightValue={tlSp.sp}
          onRightChange={v => setTlSp(s => ({ ...s, sp: v }))}
          onConnect={doAssignTlSp}
        />
      </div>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <ConnectCard
          title="Team Leader → Distributor"
          leftLabel="Select Team Leader" leftOptions={all.team_leaders} leftValue={tlDist.tl}
          onLeftChange={v => setTlDist(s => ({ ...s, tl: v }))}
          rightLabel="Select Distributor" rightOptions={all.distributors} rightValue={tlDist.dist}
          onRightChange={v => setTlDist(s => ({ ...s, dist: v }))}
          onConnect={doAssignTlDist}
        />
        <ConnectCard
          title="Distributor → Retailer"
          leftLabel="Select Distributor" leftOptions={all.distributors} leftValue={distRet.dist}
          onLeftChange={v => setDistRet(s => ({ ...s, dist: v }))}
          rightLabel="Select Retailer" rightOptions={retailers} rightValue={distRet.ret}
          onRightChange={v => setDistRet(s => ({ ...s, ret: v }))}
          onConnect={doAssignDistRet}
        />
      </div>
      <div className="mb-6">
        <AssignSalespersonCard distributors={all.distributors} retailers={retailers} />
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Network size={16} className="text-[#a67c00]" />
          <h3 className="font-bold text-slate-900">Saved Hierarchy</h3>
        </div>

        {(data?.tree || []).length === 0 &&
          (data?.unassigned_team_leaders || []).length === 0 &&
          (data?.unassigned_distributors || []).length === 0 && (
            <div className="text-xs text-slate-400">No connections yet — use the panels above to build the hierarchy.</div>
        )}

        <div className="flex flex-col gap-4">
          {(data?.tree || []).map(rm => (
            <div key={rm.id}>
              <div className="flex items-center gap-2 font-bold text-slate-900 mb-2">
                <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-xs">RM</span> {rm.name}
              </div>
              {(rm.team_leaders || []).length === 0 && <div className="ml-2 text-xs text-slate-400 mb-2">No team leaders</div>}
              <div className="ml-2 flex flex-col gap-3">
                {(rm.team_leaders || []).map(tl => (
                  <TlBox key={tl.id} tl={tl} rmId={rm.id}
                    onUnassignRmTl={unassignRmTl} onUnassignTlSp={unassignTlSp} onUnassignTlDist={unassignTlDist}
                    onDeleteBranch={deleteBranch} />
                ))}
              </div>
            </div>
          ))}

          {(data?.unassigned_team_leaders || []).length > 0 && (
            <div className="pt-3 border-t">
              <div className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1"><ChevronRight size={12} /> Team Leaders (no RM)</div>
              <div className="flex flex-col gap-3">
                {data.unassigned_team_leaders.map(tl => (
                  <TlBox key={tl.id} tl={tl} rmId={null}
                    onUnassignRmTl={unassignRmTl} onUnassignTlSp={unassignTlSp} onUnassignTlDist={unassignTlDist}
                    onDeleteBranch={deleteBranch} />
                ))}
              </div>
            </div>
          )}

          {(data?.unassigned_distributors || []).length > 0 && (
            <div className="pt-3 border-t">
              <div className="text-xs font-bold text-slate-500 uppercase mb-2 flex items-center gap-1"><ChevronRight size={12} /> Distributors (no Team Leader)</div>
              <div className="flex flex-wrap gap-2">
                {data.unassigned_distributors.map(d => (
                  <DistBox key={d.id} node={d} tlId={null} onUnassignTlDist={unassignTlDist} />
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// View Hierarchy: lazy accordion tree + search + "Change parent"
// ════════════════════════════════════════════════════════════════════════════
const ROLE = {
  rm:   { label: "RM",          cls: "bg-purple-100 text-purple-700" },
  tl:   { label: "TL",          cls: "bg-indigo-100 text-indigo-700" },
  sp:   { label: "SP",          cls: "bg-sky-100 text-sky-700" },
  dist: { label: "Distributor", cls: "bg-amber-100 text-amber-800" },
  ret:  { label: "Retailer",    cls: "bg-emerald-100 text-emerald-700" },
};
// What a node's parent is, for the Change dialog
const PARENT = {
  tl:   { type: "rm",   label: "Regional Manager", canRemove: true },
  sp:   { type: "tl",   label: "Team Leader",      canRemove: true },
  dist: { type: "tl",   label: "Team Leader",      canRemove: true },
  ret:  { type: "dist", label: "Distributor",      canRemove: false },
};

const nk = (t, id) => `${t}:${id}`;

function countsText(n) {
  const c = n.counts || {};
  const parts = [];
  if (n.type === "rm") parts.push(`${c.tl || 0} TL`, `${c.sp || 0} SP`, `${c.dist || 0} Distributors`);
  if (n.type === "tl") parts.push(`${c.sp || 0} SP`, `${c.dist || 0} Distributors`);
  if (n.type === "dist") parts.push(`${c.ret || 0} Retailers`);
  if ((n.type === "dist" || n.type === "ret") && n.region) parts.push(n.region);
  return parts.join(" · ");
}

function RoleBadge({ type }) {
  const r = ROLE[type] || ROLE.sp;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${r.cls}`}>{r.label}</span>;
}

function TreeRow({ node, pathKey, parent, ctx }) {
  const open = ctx.expanded.has(pathKey);
  const kids = ctx.cache[nk(node.type, node.id)];
  const loading = ctx.loading.has(nk(node.type, node.id));
  const lit = ctx.highlight === pathKey;
  const sub = countsText(node);
  return (
    <div>
      <div
        data-hkey={pathKey}
        className={`flex items-center gap-2 py-2 pr-2 rounded-lg transition-colors ${lit ? "bg-amber-100 ring-2 ring-amber-300" : "hover:bg-slate-50"} ${node.has_children ? "cursor-pointer" : ""}`}
        style={{ paddingLeft: 4 }}
        onClick={() => node.has_children && ctx.toggle(pathKey, node)}
      >
        <span className="w-5 h-5 flex items-center justify-center shrink-0 text-slate-400">
          {node.has_children ? (loading ? <Loader2 size={14} className="animate-spin" /> : (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />)) : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
        </span>
        <RoleBadge type={node.type} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{node.name}</div>
          {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
        </div>
        {PARENT[node.type] && (
          <button
            onClick={(e) => { e.stopPropagation(); ctx.openChange(node, parent); }}
            className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-[#a67c00] border border-amber-200 hover:bg-amber-50 rounded-md px-2 py-1"
            title={`Change ${PARENT[node.type].label}`}
          >
            <Pencil size={11} /> Change
          </button>
        )}
      </div>
      {open && kids && (
        <div className="border-l border-slate-200 ml-[13px] pl-1">
          {kids.groups.every(g => g.nodes.length === 0) && (
            <div className="text-[11px] text-slate-400 py-1.5 pl-3">Nothing linked yet</div>
          )}
          {kids.groups.map(g => g.nodes.length > 0 && (
            <div key={g.key}>
              {kids.groups.length > 1 && (
                <div className="text-[10px] font-bold uppercase text-slate-400 pl-3 pt-1.5">{g.label} ({g.nodes.length})</div>
              )}
              {g.nodes.map(c => (
                <TreeRow key={nk(c.type, c.id)} node={c} parent={node}
                  pathKey={`${pathKey}/${nk(c.type, c.id)}`} ctx={ctx} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChangeParentDialog({ target, onClose, onSaved }) {
  const node = target?.node;
  const parent = target?.parent;
  const meta = node ? PARENT[node.type] : null;
  // Only treat the row's parent as "current" if it is the right type (root rows have none)
  const current = parent && meta && parent.type === meta.type ? parent : null;
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!node || !meta) return;
    setValue(current ? current.id : "");
    setOptions([]);
    dms.hierParents(node.type).then(r => setOptions(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Could not load options"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node && node.type, node && node.id]);

  if (!node || !meta) return null;

  const save = async () => {
    const oldId = current ? current.id : "";
    const newId = value === "__none__" ? "" : value;
    if (newId === oldId) { toast("No change"); return; }
    if (!newId && !meta.canRemove) { toast.error(`Select a ${meta.label}`); return; }
    setSaving(true);
    try {
      if (node.type === "ret") {
        await dms.bulkAssignRetailers([node.id], newId);
      } else {
        // Link the new parent first, then drop the old link (uses the normal assign APIs,
        // so salesperson ↔ distributor access stays in sync).
        if (node.type === "tl") {
          if (newId) await dms.assignRmTl({ regional_manager_id: newId, team_leader_id: node.id });
          if (oldId) await dms.unassignRmTl(oldId, node.id);
        } else if (node.type === "sp") {
          if (newId) await dms.assignTlSp({ team_leader_id: newId, salesperson_id: node.id });
          if (oldId) await dms.unassignTlSp(oldId, node.id);
        } else if (node.type === "dist") {
          if (newId) await dms.assignTlDist({ team_leader_id: newId, distributor_id: node.id });
          if (oldId) await dms.unassignTlDist(oldId, node.id);
        }
      }
      const nn = options.find(o => o.id === newId);
      toast.success(newId ? `${node.name} moved under ${nn ? nn.name : meta.label}` : `${node.name} removed from ${current.name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!node} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change {meta.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <RoleBadge type={node.type} />
            <span className="font-semibold text-slate-900 truncate">{node.name}</span>
          </div>
          <div className="text-xs text-slate-500">
            Current {meta.label}: <span className="font-semibold text-slate-800">{current ? current.name : "None"}</span>
          </div>
          <select
            value={value}
            onChange={e => setValue(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">{options.length ? `Select ${meta.label}` : "Loading..."}</option>
            {meta.canRemove && current && <option value="__none__">No {meta.label} (remove link)</option>}
            {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {node.type === "sp" && (
            <p className="text-[11px] text-slate-500">The salesperson gets access to the new Team Leader's distributors and loses the old Team Leader's.</p>
          )}
          {node.type === "ret" && (
            <p className="text-[11px] text-slate-500">If the retailer's salesperson does not work with the new distributor, assign a new salesperson under "Edit links".</p>
          )}
          <div className="flex gap-2 justify-end pt-1">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button className={GOLD_BTN} onClick={save} disabled={saving || !value}>{saving ? "Saving..." : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HierarchyBrowser({ version, onChanged }) {
  const [cache, setCache] = useState({});          // "type:id" | "root" -> {groups}
  const [loading, setLoading] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set()); // path keys
  const [highlight, setHighlight] = useState("");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [change, setChange] = useState(null);
  const cacheRef = useRef({});
  cacheRef.current = cache;
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  const fetchKids = useCallback(async (type, id) => {
    const key = type === "root" ? "root" : nk(type, id);
    setLoading(s => new Set(s).add(key));
    try {
      const r = await dms.hierChildren(type, id);
      setCache(c => ({ ...c, [key]: r }));
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
      return null;
    } finally {
      setLoading(s => { const n = new Set(s); n.delete(key); return n; });
    }
  }, []);

  // Reload the top level + every node that is currently open (counts may have changed)
  const refreshAll = useCallback(async () => {
    const keys = new Set();
    expandedRef.current.forEach(p => keys.add(p.split("/").pop()));
    setCache({});
    await Promise.all([
      fetchKids("root"),
      ...[...keys].map(k => { const i = k.indexOf(":"); return fetchKids(k.slice(0, i), k.slice(i + 1)); }),
    ]);
  }, [fetchKids]);

  useEffect(() => { refreshAll(); }, [version, refreshAll]);

  const toggle = useCallback((pathKey, node) => {
    setExpanded(s => {
      const n = new Set(s);
      if (n.has(pathKey)) n.delete(pathKey); else n.add(pathKey);
      return n;
    });
    if (!cacheRef.current[nk(node.type, node.id)]) fetchKids(node.type, node.id);
  }, [fetchKids]);

  // Search (debounced)
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      dms.hierSearch(term).then(r => setHits(r.data || [])).catch(() => setHits([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const openHit = async (hit) => {
    const chain = (hit.paths && hit.paths[0]) || [];
    setHits(null); setQ("");
    let path = "";
    const toOpen = [];
    for (const p of chain) {
      path = path ? `${path}/${nk(p.type, p.id)}` : nk(p.type, p.id);
      toOpen.push(path);
      if (!cacheRef.current[nk(p.type, p.id)]) await fetchKids(p.type, p.id);
    }
    setExpanded(s => { const n = new Set(s); toOpen.forEach(k => n.add(k)); return n; });
    const target = path ? `${path}/${nk(hit.type, hit.id)}` : nk(hit.type, hit.id);
    setHighlight(target);
    setTimeout(() => {
      const el = document.querySelector(`[data-hkey="${window.CSS && CSS.escape ? CSS.escape(target) : target}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    setTimeout(() => setHighlight(h => (h === target ? "" : h)), 4000);
  };

  const ctx = { cache, loading, expanded, highlight, toggle, openChange: (node, parent) => setChange({ node, parent }) };
  const root = cache.root;

  return (
    <Card className="p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-3">
        <Network size={16} className="text-[#a67c00]" />
        <h3 className="font-bold text-slate-900">View Hierarchy</h3>
      </div>

      <div className="relative mb-3">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search any name (RM, TL, salesperson, distributor, retailer)"
          className="w-full border border-slate-300 rounded-lg pl-9 pr-9 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
        />
        {q && (
          <button onClick={() => { setQ(""); setHits(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
            <X size={14} />
          </button>
        )}
        {hits !== null && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-auto">
            {searching && hits.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Searching...</div>}
            {!searching && hits.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No match</div>}
            {hits.map(h => (
              <button key={nk(h.type, h.id)} onClick={() => openHit(h)}
                className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center gap-2 border-b last:border-b-0 border-slate-100">
                <RoleBadge type={h.type} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{h.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {h.under ? `Under: ${h.under}` : "Top level / not linked"}
                    {h.paths && h.paths.length > 1 ? ` (+${h.paths.length - 1} more)` : ""}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {!root && <div className="text-xs text-slate-400 py-4 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading...</div>}
      {root && root.groups.map(g => (
        <div key={g.key} className="mb-3 last:mb-0">
          <div className="text-[11px] font-bold uppercase text-slate-500 mb-1 px-1">{g.label} ({g.nodes.length})</div>
          {g.nodes.length === 0 && <div className="text-xs text-slate-400 px-1">None</div>}
          <div className="divide-y divide-slate-100">
            {g.nodes.map(n => (
              <TreeRow key={nk(n.type, n.id)} node={n} parent={null} pathKey={nk(n.type, n.id)} ctx={ctx} />
            ))}
          </div>
        </div>
      ))}

      <ChangeParentDialog
        target={change}
        onClose={() => setChange(null)}
        onSaved={() => { setChange(null); refreshAll(); onChanged && onChanged(); }}
      />
    </Card>
  );
}

export function OwnerHierarchyPage() {
  const [treeVer, setTreeVer] = useState(0);
  const [editVer, setEditVer] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const bumpTree = useCallback(() => setTreeVer(v => v + 1), []);
  const bumpEdit = useCallback(() => setEditVer(v => v + 1), []);

  return (
    <div>
      <PageHeader title="Organization Hierarchy"
        subtitle="Tap a name to see who is under them. Use Change to move someone."
        action={<Button variant="outline" onClick={bumpTree}>Refresh</Button>} />

      <HierarchyBrowser version={treeVer} onChanged={bumpEdit} />

      <div className="mt-6">
        <button
          onClick={() => setEditOpen(o => !o)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
        >
          <span className="flex items-center gap-2 font-bold text-slate-900">
            <Network size={16} className="text-[#a67c00]" /> Edit links
          </span>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            Connect forms and full view {editOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </button>
        {editOpen && (
          <div className="mt-3">
            <EditLinksSection key={editVer} onChanged={bumpTree} />
          </div>
        )}
      </div>
    </div>
  );
}
